import { useEffect, useState } from 'react'

/**
 * Loads /images/{slug}/{pose}.png, then .jpg (D-125: art arrives as either), and
 * falls back to .svg (SPEC 6.0). Real art is therefore a file drop with zero
 * code changes. `mirror` flips horizontally —
 * that is how the fight scene makes animal B face animal A (SPEC 6.3), and it is
 * the ONLY place a flip happens (D-048).
 *
 * The roster (public/roster.json) records which file exists at each slot at
 * build time. When it says the slot is still a placeholder the loader starts at
 * the .svg instead of asking for a .png that is not there — a request the SPA
 * rewrite answers with index.html, status 200, which then fails to decode and
 * costs a second round trip. The fallback chain stays, so a wrong hint only
 * costs what every load used to.
 */

const artIndex = new Map()

/** Called once with the roster's animals so every image starts at the right file. */
export function indexArt(animals = []) {
  for (const a of animals) if (a.art) artIndex.set(a.slug, a.art)
}

const EXTS = { pose: ['png', 'jpg', 'svg'], photo: ['jpg', 'svg'] }

function startIndex(slug, pose, kind) {
  const hint = artIndex.get(slug)?.[kind === 'photo' ? 'photo' : pose]
  const i = hint ? EXTS[kind].indexOf(hint) : 0
  return i < 0 ? 0 : i
}

export default function AnimalImage({ slug, pose = 'neutral', kind = 'pose', mirror = false, className = '', alt = '', lazy = false }) {
  const base = kind === 'photo' ? `/images/${slug}/photo` : `/images/${slug}/${pose}`
  const exts = EXTS[kind === 'photo' ? 'photo' : 'pose']
  const [i, setI] = useState(() => startIndex(slug, pose, kind))
  const [portrait, setPortrait] = useState(false)
  useEffect(() => {
    setI(startIndex(slug, pose, kind))
    setPortrait(false)
  }, [slug, pose, kind])
  const ext = exts[Math.min(i, exts.length - 1)]
  // Placeholders carry the animal's name as text; mirroring one makes it read
  // backwards. Real art (.png/.jpg) has no text (SPEC 6.1), so it mirrors freely.
  const doMirror = mirror && ext !== 'svg'
  // A frame that crops (the fact page's photo, D-126) has to know when the
  // picture is taller than it is wide, because cropping one of those to a wide
  // frame throws the animal away — the sloth's photo became a square of sky
  // (D-132). Measured on load rather than recorded, so it is right for whatever
  // file is dropped in.
  const onLoad = (e) => setPortrait(e.currentTarget.naturalHeight > e.currentTarget.naturalWidth)
  return (
    <img
      src={`${base}.${ext}`}
      onLoad={onLoad}
      onError={() => setI((n) => (n < exts.length - 1 ? n + 1 : n))}
      alt={alt}
      draggable="false"
      loading={lazy ? 'lazy' : 'eager'}
      decoding="async"
      className={`animal-img${doMirror ? ' animal-img--mirror' : ''}${portrait ? ' animal-img--portrait' : ''} ${className}`}
    />
  )
}
